import { Plus, Trash2 } from 'lucide-react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { useState } from 'react';

import { Option } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  EditableDatatable,
  NEW_ROW_ID,
  NEW_ROW_INDEX,
} from '@@/datatables/editable/EditableDatatable';

import type {
  KubernetesRbacFormValues,
  KubernetesUnifiedAccess,
} from './types';
import { useColumns } from './useColumns';

const accessStorageKey = 'k8s-rbac-access-list';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface AccessListDatatableProps {
  userOptions: Option<number>[];
  teamOptions: Option<number>[];
  roleOptions: Option<number>[];
}

export function AccessListDatatable({
  userOptions,
  teamOptions,
  roleOptions,
}: AccessListDatatableProps) {
  const {
    values: { accessList = [] },
    setFieldValue,
  } = useFormikContext<KubernetesRbacFormValues>();
  const tableState = useTableState(accessSettingsStore, accessStorageKey);
  const [newIdCounter, setNewIdCounter] = useState(NEW_ROW_ID);

  return (
    <EditableDatatable
      title="Access list"
      dataset={accessList}
      columns={useColumns({
        accessList,
        onChange,
        onRemove: (item) => handleRemoveAccess([item]),
        userOptions,
        teamOptions,
        roleOptions,
      })}
      acceptRow={acceptRow}
      revertRow={revertRow}
      getRowId={(row) => `${row.Id}`}
      settingsManager={tableState}
      renderTableActions={(selectedRows) => (
        <>
          <Button
            icon={Plus}
            color="primary"
            size="small"
            onClick={addAccess}
            disabled={false}
            data-cy="k8s-rbac-access-datatable-add-access-button"
            className="!ml-0"
          >
            Add Access
          </Button>
          <Button
            color="dangerlight"
            size="small"
            icon={Trash2}
            onClick={() => handleRemoveAccess(selectedRows)}
            data-cy="k8s-rbac-access-datatable-remove-access-button"
            disabled={selectedRows.length === 0}
          >
            Remove
          </Button>
        </>
      )}
      data-cy="k8s-rbac-access-datatable"
      emptyContentLabel="No access configured. Add access for users, teams and/or registries above."
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function onChange(val: KubernetesUnifiedAccess[]) {
    return setFieldValue('accessList', val);
  }

  function handleRemoveAccess(items: KubernetesUnifiedAccess[]) {
    const idsToRemove = new Set(
      items.map((item) => `${item.Type}-${item.PartyId}`)
    );
    const newAccessList = accessList.filter(
      (access) => !idsToRemove.has(`${access.Type}-${access.PartyId}`)
    );
    onChange(newAccessList);
  }

  function revertRow() {
    onChange(accessList.slice(1));
  }

  function acceptRow() {
    const existingRowIndex = accessList.findIndex(
      (a, index) =>
        index !== NEW_ROW_INDEX &&
        a.Type === accessList[NEW_ROW_INDEX].Type &&
        a.Name === accessList[NEW_ROW_INDEX].Name
    );
    const newRow = cloneDeep(accessList[NEW_ROW_INDEX]);

    if (existingRowIndex === -1) {
      newRow.Id = accessList.length;
      const newAccessList = [newRow, ...accessList.slice(1)];
      onChange(newAccessList);
    } else {
      const newAccessList = accessList.slice(1);
      newAccessList[existingRowIndex - 1].Role = newRow.Role;
      onChange(newAccessList);
    }
  }

  function addAccess() {
    const newAccessRow: KubernetesUnifiedAccess = {
      Id: newIdCounter,
      PartyId: null,
      Type: '',
      Name: '',
      Role: {
        Id: null,
        Name: '',
      },
    };

    const newAccessList = [cloneDeep(newAccessRow), ...accessList];
    onChange(newAccessList);
    setNewIdCounter((count) => count - 1);
  }
}
